import { useCallback, useEffect, useState } from "react";

type UseFetchResult<T> = {
	data: T | null;
	loading: boolean;
	error: string | null;
	refetch: () => void;
};

export function useFetch<T>(url: string): UseFetchResult<T> {
	const [data, setData] = useState<T | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [trigger, setTrigger] = useState(0);

	const refetch = useCallback(() => {
		setTrigger((n) => n + 1);
	}, []);

	// biome-ignore lint/correctness/useExhaustiveDependencies: trigger forces re-fetch on refetch() calls
	useEffect(() => {
		const controller = new AbortController();

		async function doFetch() {
			setLoading(true);
			setError(null);
			try {
				const res = await fetch(url, {
					credentials: "include",
					signal: controller.signal,
				});
				if (!res.ok) {
					const body = await res.json().catch(() => null);
					setError(
						(body as { error?: string } | null)?.error ??
							`Request failed (${res.status})`,
					);
					setData(null);
					return;
				}
				const json = (await res.json()) as T;
				setData(json);
			} catch (err) {
				if (err instanceof DOMException && err.name === "AbortError") return;
				setError(err instanceof Error ? err.message : "Unknown error");
				setData(null);
			} finally {
				if (!controller.signal.aborted) {
					setLoading(false);
				}
			}
		}

		doFetch();

		return () => {
			controller.abort();
		};
	}, [url, trigger]);

	return { data, loading, error, refetch };
}
